import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Loader } from "@/components/shared";

import Home from "@/pages/Home";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AdminLogin from "@/pages/auth/AdminLogin";

import MemberLayout from "@/pages/member/MemberLayout";
import Dashboard from "@/pages/member/Dashboard";
import Events from "@/pages/member/Events";
import MyPredictions from "@/pages/member/MyPredictions";
import Balance from "@/pages/member/Balance";
import Deposit from "@/pages/member/Deposit";
import Withdrawal from "@/pages/member/Withdrawal";
import Protection from "@/pages/member/Protection";
import Spin from "@/pages/member/Spin";
import Aviator from "@/pages/member/Aviator";
import ThreeCups from "@/pages/member/ThreeCups";
import DealOrNoDeal from "@/pages/member/DealOrNoDeal";
import Referral from "@/pages/member/Referral";
import Kyc from "@/pages/member/Kyc";
import Profile from "@/pages/member/Profile";
import Settings from "@/pages/member/Settings";
import Notifications from "@/pages/member/Notifications";
import Support from "@/pages/member/Support";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminDeposits from "@/pages/admin/AdminDeposits";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminPredictions from "@/pages/admin/AdminPredictions";
import AdminKyc from "@/pages/admin/AdminKyc";
import AdminSpin from "@/pages/admin/AdminSpin";
import AdminAviator from "@/pages/admin/AdminAviator";
import AdminCups from "@/pages/admin/AdminCups";
import AdminDnd from "@/pages/admin/AdminDnd";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminAds from "@/pages/admin/AdminAds";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminMisc from "@/pages/admin/AdminMisc";

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWith } = useAuth();
  const processed = React.useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(location.hash.replace("#", ""));
    const sid = params.get("session_id");
    if (!sid) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        loginWith(data.token, data.user);
        navigate(data.user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [location.hash, loginWith, navigate]);
  return <div className="min-h-screen ec-bg flex items-center justify-center"><Loader label="Signing you in..." /></div>;
}

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen ec-bg"><Loader /></div>;
  if (!user) return <Navigate to={role === "admin" ? "/admin/login" : "/login"} replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (role === "member" && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      <Route element={<Protected role="member"><MemberLayout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/events" element={<Events />} />
        <Route path="/my-predictions" element={<MyPredictions />} />
        <Route path="/balance" element={<Balance />} />
        <Route path="/deposit" element={<Deposit />} />
        <Route path="/withdrawal" element={<Withdrawal />} />
        <Route path="/protection" element={<Protection />} />
        <Route path="/spin" element={<Spin />} />
        <Route path="/aviator" element={<Aviator />} />
        <Route path="/three-cups" element={<ThreeCups />} />
        <Route path="/deal-or-no-deal" element={<DealOrNoDeal />} />
        <Route path="/referral" element={<Referral />} />
        <Route path="/kyc" element={<Kyc />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/support" element={<Support />} />
      </Route>

      <Route element={<Protected role="admin"><AdminLayout /></Protected>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/members" element={<AdminMembers />} />
        <Route path="/admin/deposits" element={<AdminDeposits />} />
        <Route path="/admin/withdrawals" element={<AdminWithdrawals />} />
        <Route path="/admin/events" element={<AdminEvents />} />
        <Route path="/admin/predictions" element={<AdminPredictions />} />
        <Route path="/admin/kyc" element={<AdminKyc />} />
        <Route path="/admin/spin" element={<AdminSpin />} />
        <Route path="/admin/aviator" element={<AdminAviator />} />
        <Route path="/admin/three-cups" element={<AdminCups />} />
        <Route path="/admin/deal-or-no-deal" element={<AdminDnd />} />
        <Route path="/admin/referrals" element={<AdminReferrals />} />
        <Route path="/admin/ads" element={<AdminAds />} />
        <Route path="/admin/payments" element={<AdminPayments />} />
        <Route path="/admin/support" element={<AdminSupport />} />
        <Route path="/admin/misc" element={<AdminMisc />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRouter />
            <Toaster position="top-center" richColors />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
